import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './ScrollRail.module.css';

const WINDOW_SIZE = 5;
const SLOT_HEIGHT = 10; // px — matches CSS .tickSlot height

export interface ScrollRailItem {
  id: string;
  label: string;
}

export interface ScrollRailProps {
  /** All anchor items — ticks + popover list are derived from this */
  items: ScrollRailItem[];
  /** Id of the currently active item (tracks scroll position) */
  activeId: string | null;
  /** Called when the user clicks an item (tick or popover row) to jump */
  onJump: (id: string) => void;
  /** Optional — when provided, renders a "Top" button in the popover header */
  onScrollToTop?: () => void;
  className?: string;
}

/**
 * ScrollRail — a compact vertical navigation rail.
 *
 * Shows a scrollable window of 5 ticks. The active tick (current scroll
 * position) is highlighted. Hovering the rail hides the ticks and reveals
 * a full popover list of all items; clicking any row calls `onJump`.
 *
 * The component owns NO scroll logic — the consumer is responsible for
 * tracking scroll position and providing `activeId`.
 */
export function ScrollRail({ items, activeId, onJump, onScrollToTop, className }: ScrollRailProps) {
  const [windowStart, setWindowStart] = useState(0);
  const railRef = useRef<HTMLDivElement>(null);

  const activeIdx = items.findIndex((item) => item.id === activeId);

  // Keep active tick inside the visible window
  useEffect(() => {
    if (activeIdx < 0) return;
    setWindowStart((prev) => {
      if (activeIdx < prev) return activeIdx;
      if (activeIdx >= prev + WINDOW_SIZE) return activeIdx - WINDOW_SIZE + 1;
      return prev;
    });
  }, [activeIdx]);

  const clampedStart = Math.max(0, Math.min(windowStart, Math.max(0, items.length - WINDOW_SIZE)));
  const translateY = -clampedStart * SLOT_HEIGHT;

  function shiftWindow(delta: number) {
    setWindowStart((prev) =>
      Math.max(0, Math.min(prev + delta, Math.max(0, items.length - WINDOW_SIZE)))
    );
  }

  function handleWheel(e: React.WheelEvent) {
    e.preventDefault();
    shiftWindow(e.deltaY > 0 ? 1 : -1);
  }

  if (items.length === 0) return null;

  return (
    <div ref={railRef} className={clsx(styles.root, className)} onWheel={handleWheel}>

      {/* Tick window — passive position indicator */}
      <div className={styles.window}>
        <div className={styles.track} style={{ '--rail-translate-y': `${translateY}px` } as React.CSSProperties}>
          {items.map((item) => (
            <button
              key={item.id}
              className={clsx(styles.tickSlot, item.id === activeId && styles.active)}
              aria-label={`Jump to ${item.label}`}
              aria-current={item.id === activeId ? 'true' : undefined}
              onClick={() => onJump(item.id)}
            >
              <span className={styles.tick} />
            </button>
          ))}
        </div>
      </div>

      {/* Popover — full list, shown on hover via CSS */}
      <div className={styles.popover} role="navigation" aria-label="Document outline">
        <div className={styles.popoverHeader}>
          <span>Jump to</span>
          {onScrollToTop && (
            <button className={styles.popoverTopBtn} onClick={onScrollToTop} aria-label="Top">
              Top
            </button>
          )}
        </div>
        {items.map((item) => (
          <button
            key={item.id}
            className={clsx(styles.popoverItem, item.id === activeId && styles.active)}
            aria-label={`Go to ${item.label}`}
            onClick={() => onJump(item.id)}
          >
            <span className={styles.popoverItemTick} aria-hidden="true" />
            <span className={styles.popoverItemLabel}>{item.label}</span>
          </button>
        ))}
      </div>

    </div>
  );
}
